import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaGoogle, FaApple, FaFacebook, FaEye, FaEyeSlash } from 'react-icons/fa';
import Header from './Header';
import Footer from './Footer';
import useAuthStore from '../store/authStore';
import { getAuth, createUserWithEmailAndPassword } from 'firebase/auth';
import { useDarkMode } from '../contexts/DarkModeContext';
import googleAuth from '../utils/googleAuth';
import { apiPost } from '../utils/api';

// Minimal production-only client error reporter
const safeReportError = async (message, context = {}) => {
  try {
    if (process.env.NODE_ENV !== 'production') return;
    const payload = {
      message: String(message || 'unknown_error'),
      context: { location: typeof window !== 'undefined' ? (window.location?.pathname || '/') : '/', ...context },
      ts: Date.now(),
    };
    await fetch('/api/client-logs', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      keepalive: true,
    });
  } catch (_) {}
};

// Login component with enhanced security and UX

const Auth = () => {
  const navigate = useNavigate();
  const { isDarkMode } = useDarkMode();
  const [isLogin, setIsLogin] = useState(true);
  const [resetMode, setResetMode] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [resetMessage, setResetMessage] = useState('');

  const [formData, setFormData] = useState({
    username: '',
    email: '',
    password: '',
    confirmPassword: ''
  });

  // Add state for password reset flow
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  // Initialize Google Auth
  useEffect(() => {
    const initGoogleAuth = async () => {
      try {
        await googleAuth.initialize();
      } catch (error) {
        safeReportError('google_auth_initialize_failed');
      }
    };
    initGoogleAuth();
  }, []);

  // Scroll to top on page load/refresh and navigation
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Additional useEffect to handle navigation from other pages
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    // Enhanced sanitization to prevent XSS and injection attacks
    const sanitizedValue = value
      .replace(/[<>]/g, '') // Remove angle brackets
      .replace(/javascript:/gi, '') // Remove javascript: protocol
      .replace(/data:/gi, '') // Remove data: protocol
      .replace(/vbscript:/gi, '') // Remove vbscript: protocol
      .replace(/on\w+=/gi, '') // Remove event handlers
      .replace(/[\x00-\x1F\x7F]/g, '') // Remove control characters
      .replace(/[&<>"']/g, '') // Remove HTML entities
      .trim(); // Remove leading/trailing whitespace
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
  };

  // 🛡️ REMOVED CLIENT-SIDE VALIDATION - Now handled server-side for security
  // Basic client-side checks only (server-side will do thorough validation)

  // Handle Google Sign-In
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setMessage('');

    try {
      // Initialize Google Auth if not already done
      await googleAuth.initialize();
      
      // Trigger Google Sign-In
      const response = await googleAuth.signIn();
      
      if (response && response.access_token) {
        // Validate access token format
        if (typeof response.access_token !== 'string' || response.access_token.length < 10) {
          throw new Error('Invalid access token received');
        }

        // Get user info from Google with enhanced validation
        const userInfo = await googleAuth.getUserInfo(response.access_token);
        
        // Enhanced user info validation
        if (!userInfo || !userInfo.email || !userInfo.id) {
          throw new Error('Invalid user information received from Google');
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(userInfo.email)) {
          throw new Error('Invalid email format received from Google');
        }

        // Create user data for your backend with sanitization
        const userData = {
          email: userInfo.email.trim(),
          name: (userInfo.name || '').trim().substring(0, 100),
          picture: userInfo.picture || '',
          googleId: userInfo.id.toString(),
          provider: 'google'
        };

        // Send to your backend for authentication using apiPost utility
        const authResponse = await apiPost('/api/auth/google', userData);

        if (authResponse.ok) {
          const result = await authResponse.json();
          
          // Store the user in auth store (token is handled via cookies)
          const { setUser } = useAuthStore.getState();
          setUser(result.user);

          // Small delay to ensure cookie propagation in dev
          setTimeout(() => navigate('/'), 0);
        } else {
          const errorData = await authResponse.json();
          setMessage(errorData.message || 'Google login failed');
        }
      }
    } catch (error) {
      setMessage('Google login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Handle password change after reset
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setMessage('');

    try {
      // Validate new password
      if (newPassword !== confirmNewPassword) {
        setMessage('New passwords do not match');
        return;
      }

      if (newPassword.length < 8) {
        setMessage('Password must be at least 8 characters long');
        return;
      }

      // Check for suspicious patterns
      const suspiciousPatterns = /(javascript:|data:|vbscript:|on\w+=|<script|<\/script)/gi;
      if (suspiciousPatterns.test(newPassword)) {
        setMessage('Invalid password format detected');
        return;
      }

      // Validate reset token format
      const isValidToken = typeof resetToken === 'string' && /^[A-Za-z0-9._-]{10,2048}$/.test(resetToken);
      if (!isValidToken) {
        setMessage('Invalid or missing reset token');
        setIsChangingPassword(false);
        return;
      }

      // Change password on backend
      const response = await apiPost('/api/auth/reset-password', {
        token: resetToken,
        newPassword: newPassword 
      }, { credentials: 'include' });

      const data = await response.json();

      if (response.ok) {
        setMessage('Password changed successfully! You can now login with your new password.');
        
        // Try to sync Firebase password
        try {
          const { syncPassword } = useAuthStore.getState();
          await syncPassword(formData.email, newPassword);
        } catch (syncError) {
          safeReportError('firebase_password_sync_failed');
          // This is not critical - user can still login
        }
        
        // Reset the form
        setResetMode(false);
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
        setIsChangingPassword(false);
        
        // Clear the form data
        setFormData({
          username: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        setMessage(data.message || 'Failed to change password. Please try again.');
      }
    } catch (err) {
      safeReportError('password_change_error');
      setMessage('An error occurred while changing your password. Please try again.');
    } finally {
      setIsChangingPassword(false);
    }
  };

  // Check if we have a reset token in URL (when user clicks reset link)
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get('token');
    const email = urlParams.get('email');
    
    if (token && email) {
      setResetToken(token);
      setFormData(prev => ({ ...prev, email: email }));
      setResetMode(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage('');
    setResetMessage('');

    // 🛡️ ENHANCED CLIENT-SIDE SECURITY CHECKS
    // Basic client-side checks only (server-side will do thorough validation)
    
    // Enhanced input validation
    if (!formData.email.trim()) {
      setMessage('Please enter an email address');
      return;
    }

    // Enhanced email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email.trim())) {
      setMessage('Please enter a valid email address');
      return;
    }

    if (!isLogin && !resetMode && formData.password !== formData.confirmPassword) {
      setMessage('Passwords do not match');
      return;
    }

    if (!resetMode && !formData.password.trim()) {
      setMessage('Please enter a password');
      return;
    }

    // Enhanced password strength validation
    if (!resetMode && formData.password.trim().length < 8) {
      setMessage('Password must be at least 8 characters long');
      return;
    }

    // Check for suspicious patterns in inputs
    const suspiciousPatterns = /(javascript:|data:|vbscript:|on\w+=|<script|<\/script)/gi;
    if (suspiciousPatterns.test(formData.email) || suspiciousPatterns.test(formData.username) || suspiciousPatterns.test(formData.password)) {
      setMessage('Invalid input detected');
      return;
    }

    setLoading(true);
    setResetLoading(false);

    try {
      if (resetMode) {
        // Handle password reset
        const response = await apiPost('/api/auth/forgot-password', { email: formData.email.trim() }, { credentials: 'include' });

        const data = await response.json();

        if (response.ok) {
          setResetMessage('Password reset email sent successfully! Please check your inbox.');
          setFormData({ ...formData, email: '' });
        } else {
          let errorMessage = 'An error occurred. Please try again.';
          if (response.status === 404) {
            errorMessage = 'No account found with this email address.';
          } else if (data.message && typeof data.message === 'string') {
            errorMessage = data.message;
          }
          setResetMessage(errorMessage);
        }
      } else {
        // Handle login/signup using authStore
        const authStore = useAuthStore.getState();
        
        if (isLogin) {
          // Login
          await authStore.login(formData.email.trim(), formData.password);
          setMessage('Success! Redirecting...');
          setTimeout(() => {
            navigate('/');
          }, 1000);
        } else {
          // Sign up - handle username conflicts properly
          try {
            // First try to register with backend (including username)
            const backendRes = await apiPost('/api/auth/register', {
              username: formData.username.trim(),
              email: formData.email.trim(),
              password: formData.password
            }, { credentials: 'include' });

            const backendData = await backendRes.json();

            if (!backendRes.ok) {
              // Handle specific backend errors
              if (backendRes.status === 409) {
                if (backendData.message && backendData.message.includes('username')) {
                  throw new Error('Username already taken. Please choose a different username.');
                } else {
                  throw new Error('An account with this email already exists. Please try logging in instead.');
                }
              } else if (backendRes.status === 429) {
                throw new Error('Too many registration attempts. Please wait a few minutes before trying again.');
              } else {
                // Handle validation errors with specific messages
                let errorMessage = 'Registration failed. Please try again.';
                
                if (backendData.error && backendData.error.message) {
                  errorMessage = backendData.error.message;
                } else if (backendData.message) {
                  errorMessage = backendData.message;
                } else if (backendData.details && backendData.details.length > 0) {
                  // Get the first validation error message
                  errorMessage = backendData.details[0].message;
                }
                
                throw new Error(errorMessage);
              }
            }
            
            // If backend registration succeeds, set user data directly
            // The backend should handle Firebase account creation
            try {
              const user = backendData.user;
              const authStore = useAuthStore.getState();
              authStore.setUser(user);
              
              setMessage('Success! Redirecting...');
          setTimeout(() => {
            navigate('/');
              }, 1000);
            } catch (userError) {
              setMessage('Account created but login failed. Please try logging in manually.');
            }
          } catch (error) {
            // Handle Firebase errors that might occur after backend success
            if (error.code === 'auth/email-already-in-use') {
              setMessage('An account with this email already exists. Please try logging in instead.');
            } else {
              throw error; // Re-throw other errors to be handled by the main catch block
            }
          }
        }
      }
    } catch (error) {
      // 🛡️ ENHANCED SECURITY: Prevent information disclosure in error messages
      safeReportError('authentication_error');
      
      // Convert Firebase errors to user-friendly messages without exposing system details
      let errorMessage = 'An error occurred. Please try again.';
      
      if (error.code) {
        switch (error.code) {
          case 'auth/user-not-found':
          case 'auth/wrong-password':
          case 'auth/invalid-email':
          case 'auth/invalid-credential':
            errorMessage = 'Invalid email or password. Please check your credentials and try again.';
            break;
          case 'auth/email-already-in-use':
            errorMessage = 'An account with this email already exists. Please try logging in instead.';
            break;
          case 'auth/weak-password':
            errorMessage = 'Password is too weak. Please choose a stronger password.';
            break;
          case 'auth/too-many-requests':
            errorMessage = 'Too many failed attempts. Please wait a moment before trying again.';
            break;
          case 'auth/network-request-failed':
            errorMessage = 'Network error. Please check your internet connection and try again.';
            break;
          case 'auth/user-disabled':
            errorMessage = 'This account has been disabled. Please contact support.';
            break;
          default:
            // For other Firebase errors, show a generic message
              errorMessage = 'Authentication failed. Please check your credentials and try again.';
        }
      } else if (error.message) {
        // Handle non-Firebase errors with enhanced security
        if (error.message.includes('Backend login failed') || error.message.includes('Backend registration failed')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Username already taken')) {
          errorMessage = 'Username already taken. Please choose a different username.';
        } else if (error.message.includes('email already exists')) {
          errorMessage = 'An account with this email already exists. Please try logging in instead.';
        } else if (error.message.includes('Invalid access token') || error.message.includes('Invalid user information')) {
          errorMessage = 'Google authentication failed. Please try again.';
        } else if (error.message.includes('Invalid email or password') || error.message.includes('Login failed') || error.message.includes('Email or password is incorrect')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Too many requests') || error.message.includes('Too Many Requests') || error.message.includes('rate limit') || error.message.includes('Rate limit') || error.message.includes('Too many failed login attempts')) {
          errorMessage = 'Too many login attempts. Please wait a few minutes before trying again.';
        } else if (error.message.includes('This username is reserved for official use') ||
                   error.message.includes('This username impersonates an official role') ||
                   error.message.includes('This username contains prohibited content') ||
                   error.message.includes('This username impersonates the brand with numbers') ||
                   error.message.includes('This username is not allowed')) {
          // Preserve specific blocked username error messages
          errorMessage = error.message;
        } else {
          // Generic message for any other errors to prevent information disclosure
          errorMessage = 'An error occurred. Please try again.';
        }
      }
      
      if (resetMode) {
        setResetMessage(errorMessage);
      } else {
        setMessage(errorMessage);
      }
      } finally {
        setLoading(false);
      setResetLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setResetLoading(true);
    setResetMessage('');
    
    try {
      // Enhanced email validation for password reset
      const email = formData.email.trim();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      
      if (!email || !emailRegex.test(email)) {
        setResetMessage('Please enter a valid email address');
        return;
      }

      // Check for suspicious patterns
      const suspiciousPatterns = /(javascript:|data:|vbscript:|on\w+=|<script|<\/script)/gi;
      if (suspiciousPatterns.test(email)) {
        setResetMessage('Invalid email format detected');
        return;
      }

      const res = await apiPost('/api/auth/forgot-password', { email: email }, { credentials: 'include' });
      
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to send reset email');
      setResetMessage('Password reset email sent! Please check your inbox.');
    } catch (err) {
      // Enhanced error handling to prevent information disclosure
      safeReportError('password_reset_request_error');
      setResetMessage('An error occurred while processing your request. Please try again.');
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode ? 'bg-[#0f0f0f]' : 'bg-gray-50'
    }`}>
      <Header />
      <div className="flex-1 flex items-center justify-center p-4 pt-8 sm:pt-12 lg:pt-16">
        <div className={`w-full max-w-7xl flex flex-col lg:flex-row rounded-3xl shadow-2xl overflow-hidden transition-all duration-500 ${
          isDarkMode 
            ? 'bg-gradient-to-br from-[#1a1a1a] via-[#2a2a2a] to-[#1a1a1a] shadow-2xl shadow-black/50 border border-gray-700/50' 
            : 'bg-white'
        }`}>
          {/* Left Panel - Login Form */}
          <div className="w-full lg:w-1/2 flex items-center justify-center p-6 sm:p-8 lg:p-12">
            <div className="w-full max-w-md min-h-[500px] flex flex-col justify-center">
              {/* Title and Description */}
              <div className="text-center mb-6 sm:mb-8">
                <h1 className={`text-2xl sm:text-3xl font-bold mb-2 sm:mb-3 transition-colors duration-300 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>
                  {resetMode ? '🔑 Reset your password' : 'Welcome back!'}
                </h1>
                <p className={`text-sm sm:text-base transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  {resetMode 
                    ? 'Enter your email address and we\'ll send you a link to reset your password.'
                    : 'Sign in to your account to continue'
                  }
                </p>
          </div>

              {/* Login/Signup Toggle */}
              <div className={`flex rounded-lg p-1 mb-6 sm:mb-8 transition-colors duration-300 ${
                isDarkMode ? 'bg-[#2a2a2a]' : 'bg-gray-100'
              }`}>
                <button
                  onClick={() => setIsLogin(true)}
                  className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-md text-sm font-medium transition-all ${
                    isLogin 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : isDarkMode 
                        ? 'text-gray-300 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Login
                </button>
                <button
                  onClick={() => setIsLogin(false)}
                  className={`flex-1 py-2 sm:py-3 px-3 sm:px-4 rounded-md text-sm font-medium transition-all ${
                    !isLogin 
                      ? 'bg-blue-600 text-white shadow-sm' 
                      : isDarkMode 
                        ? 'text-gray-300 hover:text-white' 
                        : 'text-gray-600 hover:text-gray-900'
                  }`}
                >
                  Sign Up
                </button>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-5" autoComplete="off" spellCheck="false">
                {/* CSRF Protection */}
                <input type="hidden" name="_csrf" value={document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''} />
                
                {/* Password Change Form - Show when reset token is present */}
                {resetToken ? (
                  <>
                    <div>
                      <label htmlFor="newPassword" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          id="newPassword"
                          name="newPassword"
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300'
                          }`}
                          placeholder="Enter your new password"
                          disabled={isChangingPassword}
                          autoComplete="new-password"
                          minLength={8}
                          maxLength={128}
                          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$"
                          title="Password must be at least 8 characters with uppercase, lowercase, and number"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {showPassword ? <FaEyeSlash size={16} className="sm:w-5 sm:h-5" /> : <FaEye size={16} className="sm:w-5 sm:h-5" />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmNewPassword" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-200' : 'text-gray-700'
                      }`}>
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          id="confirmNewPassword"
                          name="confirmNewPassword"
                          type={showPassword ? 'text' : 'password'}
                          required
                          value={confirmNewPassword}
                          onChange={(e) => setConfirmNewPassword(e.target.value)}
                          className={`w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                            isDarkMode 
                              ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                              : 'border-gray-300'
                          }`}
                          placeholder="Confirm your new password"
                          disabled={isChangingPassword}
                          autoComplete="new-password"
                          minLength={8}
                          maxLength={128}
                          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$"
                          title="Password must be at least 8 characters with uppercase, lowercase, and number"
                        />
                        <button
                          type="button"
                          onClick={() => setShowPassword(!showPassword)}
                          className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                            isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                          }`}
                        >
                          {showPassword ? <FaEyeSlash size={16} className="sm:w-5 sm:h-5" /> : <FaEye size={16} className="sm:w-5 sm:h-5" />}
                        </button>
                      </div>
                    </div>

                    {/* Submit Button for Password Change */}
                    <button
                      type="button"
                      onClick={handlePasswordChange}
                      className="w-full py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm text-sm sm:text-base"
                      disabled={isChangingPassword}
                    >
                      {isChangingPassword ? (
                        <span className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Changing Password...
                        </span>
                      ) : (
                        'Change Password'
                      )}
                    </button>

                    {/* Back to Login button */}
                    <button
                      type="button"
                      className={`w-full py-2 sm:py-3 font-medium rounded-lg transition-colors text-sm sm:text-base ${
                        isDarkMode 
                          ? 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-200' 
                          : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                      }`}
                      onClick={() => { 
                        setResetMode(false); 
                        setResetToken('');
                        setNewPassword('');
                        setConfirmNewPassword('');
                        setFormData({ ...formData, email: '' }); 
                        setResetMessage(''); 
                      }}
                      disabled={isChangingPassword}
                    >
                      Back to Login
                    </button>
                  </>
                ) : (
                  <>
                {/* Username field - only show for Sign Up */}
                {!isLogin && (
                  <div>
                    <label htmlFor="username" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Username
                    </label>
                    <input
                      id="username"
                      name="username"
                      type="text"
                      required
                      value={formData.username}
                      onChange={handleChange}
                      className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                        isDarkMode 
                          ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                          : 'border-gray-300'
                      }`}
                      placeholder="Enter your username"
                      disabled={loading}
                          autoComplete="username"
                          maxLength={20}
                          pattern="[a-zA-Z0-9_-]{3,20}"
                          title="Username must be 3-20 characters, letters, numbers, hyphens, and underscores only"
                          aria-describedby="username-error"
                    />
                  </div>
                )}

                {/* Email field - show for all modes */}
                <div>
                  <label htmlFor="email" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-200' : 'text-gray-700'
                  }`}>
                    Email
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    className={`w-full px-3 sm:px-4 py-2 sm:py-3 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                      isDarkMode 
                        ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                        : 'border-gray-300'
                    }`}
                    placeholder="Enter your email"
                    disabled={loading}
                        autoComplete="email"
                        maxLength={254}
                        pattern="[a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,}$"
                        title="Please enter a valid email address"
                        aria-describedby="email-error"
                  />
                </div>

                {/* Password field - show for Login and Sign Up */}
                {!resetMode && (
                  <div>
                    <label htmlFor="password" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Password
                    </label>
                <div className="relative">
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    required
                    value={formData.password}
                    onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Enter your password"
                    disabled={loading}
                        autoComplete="current-password"
                        minLength={8}
                        maxLength={128}
                        pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$"
                        title="Password must be at least 8 characters with uppercase, lowercase, and number"
                        aria-describedby="password-error"
                  />
                  <button
                    type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {showPassword ? <FaEyeSlash size={16} className="sm:w-5 sm:h-5" /> : <FaEye size={16} className="sm:w-5 sm:h-5" />}
                  </button>
                </div>
                  </div>
                )}

                {/* Confirm Password field - only show for Sign Up */}
                {!isLogin && !resetMode && (
                  <div>
                    <label htmlFor="confirmPassword" className={`block text-sm font-medium mb-1 sm:mb-2 transition-colors duration-300 ${
                      isDarkMode ? 'text-gray-200' : 'text-gray-700'
                    }`}>
                      Confirm Password
                    </label>
                  <div className="relative">
                    <input
                      id="confirmPassword"
                      name="confirmPassword"
                      type={showPassword ? 'text' : 'password'}
                      required
                      value={formData.confirmPassword}
                      onChange={handleChange}
                        className={`w-full px-3 sm:px-4 py-2 sm:py-3 pr-10 sm:pr-12 border rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors text-sm sm:text-base ${
                          isDarkMode 
                            ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400' 
                            : 'border-gray-300'
                        }`}
                        placeholder="Confirm your password"
                      disabled={loading}
                          autoComplete="new-password"
                          minLength={8}
                          maxLength={128}
                          pattern="^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)[a-zA-Z\d@$!%*?&]{8,}$"
                          title="Password must be at least 8 characters with uppercase, lowercase, and number"
                          aria-describedby="confirm-password-error"
                    />
                    <button
                      type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className={`absolute right-3 top-1/2 -translate-y-1/2 transition-colors duration-300 ${
                          isDarkMode ? 'text-gray-400 hover:text-gray-200' : 'text-gray-400 hover:text-gray-600'
                        }`}
                      >
                        {showPassword ? <FaEyeSlash size={16} className="sm:w-5 sm:h-5" /> : <FaEye size={16} className="sm:w-5 sm:h-5" />}
                    </button>
                    </div>
                  </div>
                )}

                {/* Remember me and Forgot password - only show for Login */}
                {isLogin && !resetMode && (
                  <div className="flex items-center justify-between">
                    <label className="flex items-center">
                      <input type="checkbox" className={`h-4 w-4 text-blue-600 focus:ring-blue-500 rounded transition-colors duration-300 ${
                        isDarkMode ? 'border-gray-600 bg-[#2a2a2a]' : 'border-gray-300'
                      }`} />
                      <span className={`ml-2 text-sm transition-colors duration-300 ${
                        isDarkMode ? 'text-gray-300' : 'text-gray-600'
                      }`}>Remember me</span>
                    </label>
                      <button
                        type="button"
                      className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                      onClick={() => setResetMode(true)}
                      >
                      Forgot password?
                      </button>
                  </div>
                )}

                    {/* Submit Button for Login/Signup */}
                <button
                  type="submit"
                  className="w-full py-2 sm:py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors shadow-sm text-sm sm:text-base"
                  disabled={loading || resetLoading}
                >
                  {loading ? (
                    <span className="flex items-center justify-center">
                      <svg className="animate-spin -ml-1 mr-2 h-4 w-4 sm:h-5 sm:w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      {resetMode ? 'Sending...' : (isLogin ? 'Signing in...' : 'Creating account...')}
                    </span>
                  ) : (
                    resetMode ? 'Send Reset Email' : (isLogin ? 'Login' : 'Sign Up')
                  )}
                </button>

                {/* Back to Login button - only show for Reset mode */}
                {resetMode && (
                  <button
                    type="button"
                    className={`w-full py-2 sm:py-3 font-medium rounded-lg transition-colors text-sm sm:text-base ${
                      isDarkMode 
                        ? 'bg-[#2a2a2a] hover:bg-[#3a3a3a] text-gray-200' 
                        : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
                    }`}
                    onClick={() => { setResetMode(false); setFormData({ ...formData, email: '' }); setResetMessage(''); }}
                    disabled={resetLoading}
                  >
                    Back to Login
                  </button>
                    )}
                  </>
                )}

                {/* Error/Success Message */}
                {(message || resetMessage) && (
                  <div className={`text-center text-sm ${(message || resetMessage).includes('success') ? 'text-green-600' : 'text-red-600'}`}>
                    {message || resetMessage}
                  </div>
                )}
              </form>

              {/* Social Login */}
              <div className="mt-6 sm:mt-8">
                <div className="flex items-center mb-4 sm:mb-6">
                  <div className={`flex-1 h-px transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  }`}></div>
                  <span className={`px-4 text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-500'
                  }`}>or continue with</span>
                  <div className={`flex-1 h-px transition-colors duration-300 ${
                    isDarkMode ? 'bg-gray-600' : 'bg-gray-300'
                  }`}></div>
                </div>
                <div className="flex gap-3 sm:gap-4 justify-center">
                  {/* Google */}
                  <button 
                    onClick={handleGoogleSignIn}
                    disabled={loading}
                    className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full border flex items-center justify-center hover:bg-gray-50 transition-colors shadow-sm ${
                      isDarkMode 
                        ? 'bg-[#2a2a2a] border-gray-600 text-gray-300 hover:bg-[#3a3a3a]' 
                        : 'bg-white border-gray-300 text-gray-700'
                    } ${loading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" viewBox="0 0 24 24">
                      <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                      <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                      <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                    </svg>
                  </button>
                  {/* Apple */}
                  <button className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-black text-white flex items-center justify-center hover:bg-gray-800 transition-colors">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
                    </svg>
                  </button>
                  {/* Facebook */}
                  <button className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-[#1877F2] text-white flex items-center justify-center hover:bg-[#166FE5] transition-colors">
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.788 4.659-4.788 1.325 0 2.463.099 2.797.143v3.24l-1.918.001c-1.504 0-1.797.715-1.797 1.763v2.313h3.587l-.467 3.622h-3.12V24h6.116C23.406 24 24 23.408 24 22.674V1.326C24 .592 23.406 0 22.675 0"/>
                    </svg>
                  </button>
                </div>
              </div>

              {/* Registration Link */}
              {isLogin && (
                <p className={`mt-6 text-center text-sm transition-colors duration-300 ${
                  isDarkMode ? 'text-gray-300' : 'text-gray-600'
                }`}>
                  Not a member?{' '}
                  <button 
                    type="button" 
                    className="text-blue-600 hover:text-blue-500 font-medium"
                    onClick={() => setIsLogin(false)}
                  >
                    Register now
                  </button>
                </p>
              )}
            </div>
          </div>

          {/* Right Panel - Illustration and Promotional Content (Hidden on Mobile) */}
          <div className={`hidden lg:flex lg:w-1/2 items-center justify-center p-12 transition-all duration-500 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460]' 
              : 'bg-gradient-to-br from-blue-50 to-blue-100'
          }`}>
            <div className="w-full max-w-lg">
              {/* Main Card with White Background */}
              <div className={`rounded-3xl p-8 shadow-xl relative transition-all duration-500 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-[#2a2a2a] via-[#1a1a1a] to-[#2a2a2a] shadow-2xl shadow-black/50 border border-gray-700/50' 
                  : 'bg-white'
              }`}>
                {/* Custom Image */}
                <div className="relative mb-8">
                  <div className={`w-full h-80 rounded-2xl shadow-lg overflow-hidden transition-all duration-500 ${
                    isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'
                  }`}>
                    <img 
                      src="/images/loginimage/signup.png" 
                      alt="YTproud Social Media Marketplace" 
                      className="w-full h-full object-cover"
                      loading="lazy"
                    />
                  </div>
                  
                  {/* Floating profile icons */}
                  <div className="absolute -top-3 -left-3 w-14 h-14 bg-yellow-200 rounded-full flex items-center justify-center border-3 border-white shadow-lg">
                    <span className="text-yellow-600 text-xl">😊</span>
                  </div>
                  <div className="absolute -top-3 -right-3 w-14 h-14 bg-orange-200 rounded-full flex items-center justify-center border-3 border-white shadow-lg">
                    <span className="text-orange-600 text-xl">😮</span>
                  </div>
                </div>

                {/* Pagination Dots */}
                <div className="flex justify-center gap-2 mb-6">
                  <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    isDarkMode ? 'bg-blue-400' : 'bg-blue-400'
                  }`}></div>
                  <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    isDarkMode ? 'bg-blue-300' : 'bg-blue-300'
                  }`}></div>
                  <div className={`w-2 h-2 rounded-full transition-colors duration-300 ${
                    isDarkMode ? 'bg-blue-300' : 'bg-blue-300'
                  }`}></div>
                </div>

                {/* Promotional Message */}
                <div className="text-center">
                  <h2 className={`text-2xl font-bold mb-2 transition-colors duration-300 ${
                    isDarkMode ? 'text-white' : 'text-gray-900'
                  }`}>
                    Make your work easier and organized with{' '}
                    <span className="text-blue-600">YTproud</span>
                  </h2>
                  <p className={`text-base transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    The trusted marketplace to buy and sell social media accounts
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}

export default Auth; 